import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import yaml from 'js-yaml';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { LoadingSpinner } from '../ui/LoadingSpinner';

// Property analysis form validation schema
const propertyAnalysisSchema = z.object({
  // Property Information
  address: z.string().min(1, 'Property address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(2, 'State is required'),
  zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Valid ZIP code required'),
  propertyType: z.enum(['single-family', 'duplex', 'triplex', 'fourplex', 'multi-family', 'condo', 'townhome'], {
    required_error: 'Property type is required'
  }),
  
  // Purchase Information  
  purchasePrice: z.number().min(1000, 'Purchase price must be at least $1,000'),
  downPayment: z.number().min(0, 'Down payment cannot be negative'),
  downPaymentPercent: z.number().min(0).max(100, 'Down payment percentage must be between 0-100%'),
  
  // Property Costs
  annualPropertyTaxes: z.number().min(0, 'Property taxes cannot be negative'),
  annualInsurance: z.number().min(0, 'Insurance cannot be negative'),
  monthlyHoaFees: z.number().min(0, 'HOA fees cannot be negative').optional(),
  
  // Loan Details
  loanAmount: z.number().min(0, 'Loan amount cannot be negative'),
  interestRate: z.number().min(0.1, 'Interest rate must be at least 0.1%').max(30, 'Interest rate cannot exceed 30%'),
  loanTerm: z.number().min(1, 'Loan term must be at least 1 year').max(50, 'Loan term cannot exceed 50 years'),
  
  // Renovation Costs
  roofCosts: z.number().min(0, 'Roof costs cannot be negative').optional(),
  flooringCosts: z.number().min(0, 'Flooring costs cannot be negative').optional(),
  paintingCosts: z.number().min(0, 'Painting costs cannot be negative').optional(),
  kitchenCosts: z.number().min(0, 'Kitchen costs cannot be negative').optional(),
  bathroomCosts: z.number().min(0, 'Bathroom costs cannot be negative').optional(),
  hvacCosts: z.number().min(0, 'HVAC costs cannot be negative').optional(),
  electricalCosts: z.number().min(0, 'Electrical costs cannot be negative').optional(),
  plumbingCosts: z.number().min(0, 'Plumbing costs cannot be negative').optional(),
  generalFixUpCosts: z.number().min(0, 'General fix-up costs cannot be negative').optional(),
  
  // Analysis Preferences
  analysisType: z.enum(['buy-hold', 'flip', 'brrr'], {
    required_error: 'Analysis type is required'
  }),
  
  // Buy & Hold specific
  expectedRentPerMonth: z.number().min(0, 'Expected rent cannot be negative').optional(),
  vacancyRate: z.number().min(0).max(100, 'Vacancy rate must be between 0-100%').optional(),
  maintenanceRate: z.number().min(0).max(100, 'Maintenance rate must be between 0-100%').optional(),
  managementRate: z.number().min(0).max(100, 'Management rate must be between 0-100%').optional(),
  
  // Flip specific
  afterRepairValue: z.number().min(0, 'ARV cannot be negative').optional(),
  holdingPeriodMonths: z.number().min(1, 'Holding period must be at least 1 month').optional(),
  sellingCosts: z.number().min(0, 'Selling costs cannot be negative').optional(),
});

export type PropertyAnalysisFormData = z.infer<typeof propertyAnalysisSchema>;

interface PropertyAnalysisFormProps {
  onSubmit: (data: PropertyAnalysisFormData) => void;
  isLoading?: boolean;
  initialData?: Partial<PropertyAnalysisFormData>;
}

export const PropertyAnalysisForm: React.FC<PropertyAnalysisFormProps> = ({
  onSubmit,
  isLoading = false,
  initialData
}) => {
  const [activeTab, setActiveTab] = useState<'property' | 'loan' | 'renovation' | 'analysis'>('property');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid }
  } = useForm<PropertyAnalysisFormData>({
    resolver: zodResolver(propertyAnalysisSchema),
    defaultValues: initialData,
    mode: 'onChange'
  });

  const watchedValues = watch();
  const analysisType = watch('analysisType');
  const purchasePrice = watch('purchasePrice');
  const downPaymentPercent = watch('downPaymentPercent');

  // DEV ONLY: Load test data from YAML and populate form
  const loadTestDataFromFile = async (filename: string) => {
    try {
      const res = await fetch(`/${filename}`);
      if (!res.ok) {
        throw new Error(`Failed to fetch ${filename}: ${res.status}`);
      }
      const text = await res.text();
      const data = yaml.load(text) as Record<string, any>;
      
      if (data) {
        console.log(`Loading test data from ${filename}:`, data);
        
        // Get all valid form field names from the schema
        const validFields = Object.keys(propertyAnalysisSchema.shape) as Array<keyof PropertyAnalysisFormData>;
        
        // Load only valid fields and log any mismatches
        let loadedCount = 0;
        Object.entries(data).forEach(([key, value]) => {
          if (validFields.includes(key as keyof PropertyAnalysisFormData)) {
            setValue(key as keyof PropertyAnalysisFormData, value, { shouldValidate: true });
            loadedCount++;
          } else {
            console.warn(`Test data field "${key}" does not match any form field`);
          }
        });
        
        console.log(`Loaded ${loadedCount} test data fields`);
        
        // Show success message
        const successMsg = document.createElement('div');
        successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
        successMsg.textContent = `✅ Loaded ${loadedCount} fields from ${filename.replace('test-data', '').replace('.yaml', '') || 'default'}`;
        document.body.appendChild(successMsg);
        
        setTimeout(() => {
          document.body.removeChild(successMsg);
        }, 3000);
      }
    } catch (err) {
      console.error('Failed to load test data:', err);
      const errorMsg = document.createElement('div');
      errorMsg.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg z-50';
      errorMsg.textContent = `❌ Failed to load ${filename}: ${err instanceof Error ? err.message : 'Unknown error'}`;
      document.body.appendChild(errorMsg);
      
      setTimeout(() => {
        if (document.body.contains(errorMsg)) {
          document.body.removeChild(errorMsg);
        }
      }, 5000);
    }
  };

  // Auto-calculate loan amount and down payment
  React.useEffect(() => {
    if (purchasePrice && downPaymentPercent !== undefined) {
      const downPayment = (purchasePrice * downPaymentPercent) / 100;
      const loanAmount = purchasePrice - downPayment;
      setValue('downPayment', downPayment);
      setValue('loanAmount', loanAmount);
    }
  }, [purchasePrice, downPaymentPercent, setValue]);

  // Calculate total renovation costs
  const totalRenovationCosts = React.useMemo(() => {
    const costs = [
      watchedValues.roofCosts || 0,
      watchedValues.flooringCosts || 0,
      watchedValues.paintingCosts || 0,
      watchedValues.kitchenCosts || 0,
      watchedValues.bathroomCosts || 0,
      watchedValues.hvacCosts || 0,
      watchedValues.electricalCosts || 0,
      watchedValues.plumbingCosts || 0,
      watchedValues.generalFixUpCosts || 0,
    ];
    return costs.reduce((sum, cost) => sum + cost, 0);
  }, [watchedValues]);

  const tabs = [
    { id: 'property', label: 'Property Info', icon: '🏠' },
    { id: 'loan', label: 'Loan Details', icon: '💰' },
    { id: 'renovation', label: 'Renovation', icon: '🔨' },
    { id: 'analysis', label: 'Analysis', icon: '📊' },
  ];

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-2xl font-bold text-gray-900">Property Investment Analysis</h2>
        <p className="text-gray-600 mt-1">Enter your property details to get comprehensive investment analysis</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-6">
        {/* DEV ONLY: Test Data Buttons */}
        {import.meta.env.DEV && (
          <div className="mb-4">
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
                onClick={() => loadTestDataFromFile('test-data.yaml')}
                title="Load Buy & Hold test data"
              >
                📊 Buy & Hold
              </button>
              <button
                type="button"
                className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
                onClick={() => loadTestDataFromFile('test-data-flip.yaml')}
                title="Load Fix & Flip test data"
              >
                🔨 Fix & Flip
              </button>
              <button
                type="button"
                className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded text-sm"
                onClick={() => loadTestDataFromFile('test-data-brrr.yaml')}
                title="Load BRRR test data"
              >
                🔄 BRRR
              </button>
            </div>
          </div>
        )}

        {/* Property Information Tab */}
        {activeTab === 'property' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                  Property Address
                </label>
                <Input
                  id="address"
                  placeholder="123 Main Street"
                  {...register('address')}
                  error={errors.address?.message}
                />
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  City
                </label>
                <Input
                  id="city"
                  placeholder="Los Angeles"
                  {...register('city')}
                  error={errors.city?.message}
                />
              </div>

              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                  State
                </label>
                <Input
                  id="state"
                  placeholder="CA"
                  {...register('state')}
                  error={errors.state?.message}
                />
              </div>

              <div>
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
                  ZIP Code
                </label>
                <Input
                  id="zipCode"
                  placeholder="90210"
                  {...register('zipCode')}
                  error={errors.zipCode?.message}
                />
              </div>

              <div>
                <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700">
                  Property Type
                </label>
                <select
                  id="propertyType"
                  {...register('propertyType')}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select property type</option>
                  <option value="single-family">Single Family Home</option>
                  <option value="duplex">Duplex</option>
                  <option value="triplex">Triplex</option>
                  <option value="fourplex">Fourplex</option>
                  <option value="multi-family">Multi-Family</option>
                  <option value="condo">Condominium</option>
                  <option value="townhome">Townhome</option>
                </select>
                {errors.propertyType && (
                  <p className="mt-1 text-sm text-red-600">{errors.propertyType.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="purchasePrice" className="block text-sm font-medium text-gray-700">
                  Purchase Price
                </label>
                <Input
                  id="purchasePrice"
                  type="number"
                  placeholder="500000"
                  {...register('purchasePrice', { valueAsNumber: true })}
                  error={errors.purchasePrice?.message}
                />
              </div>

              <div>
                <label htmlFor="downPaymentPercent" className="block text-sm font-medium text-gray-700">
                  Down Payment (%)
                </label>
                <Input
                  id="downPaymentPercent"
                  type="number"
                  step="0.1"
                  placeholder="20"
                  {...register('downPaymentPercent', { valueAsNumber: true })}
                  error={errors.downPaymentPercent?.message}
                />
              </div>

              <div>
                <label htmlFor="annualPropertyTaxes" className="block text-sm font-medium text-gray-700">
                  Annual Property Taxes
                </label>
                <Input
                  id="annualPropertyTaxes"
                  type="number"
                  placeholder="6000"
                  {...register('annualPropertyTaxes', { valueAsNumber: true })}
                  error={errors.annualPropertyTaxes?.message}
                />
              </div>

              <div>
                <label htmlFor="annualInsurance" className="block text-sm font-medium text-gray-700">
                  Annual Insurance
                </label>
                <Input
                  id="annualInsurance"
                  type="number"
                  placeholder="1200"
                  {...register('annualInsurance', { valueAsNumber: true })}
                  error={errors.annualInsurance?.message}
                />
              </div>

              <div>
                <label htmlFor="monthlyHoaFees" className="block text-sm font-medium text-gray-700">
                  Monthly HOA Fees (Optional)
                </label>
                <Input
                  id="monthlyHoaFees"
                  type="number"
                  placeholder="200"
                  {...register('monthlyHoaFees', { valueAsNumber: true })}
                  error={errors.monthlyHoaFees?.message}
                />
              </div>
            </div>
          </div>
        )}

        {/* Loan Details Tab */}
        {activeTab === 'loan' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="loanAmount" className="block text-sm font-medium text-gray-700">
                  Loan Amount
                </label>
                <Input
                  id="loanAmount"
                  type="number"
                  placeholder="400000"
                  {...register('loanAmount', { valueAsNumber: true })}
                  error={errors.loanAmount?.message}
                  disabled
                  className="bg-gray-50"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-calculated based on purchase price and down payment</p>
              </div>

              <div>
                <label htmlFor="downPayment" className="block text-sm font-medium text-gray-700">
                  Down Payment Amount
                </label>
                <Input
                  id="downPayment"
                  type="number"
                  placeholder="100000"
                  {...register('downPayment', { valueAsNumber: true })}
                  error={errors.downPayment?.message}
                  disabled
                  className="bg-gray-50"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-calculated based on purchase price and percentage</p>
              </div>

              <div>
                <label htmlFor="interestRate" className="block text-sm font-medium text-gray-700">
                  Interest Rate (%)
                </label>
                <Input
                  id="interestRate"
                  type="number"
                  step="0.01"
                  placeholder="6.5"
                  {...register('interestRate', { valueAsNumber: true })}
                  error={errors.interestRate?.message}
                />
              </div>

              <div>
                <label htmlFor="loanTerm" className="block text-sm font-medium text-gray-700">
                  Loan Term (Years)
                </label>
                <Input
                  id="loanTerm"
                  type="number"
                  placeholder="30"
                  {...register('loanTerm', { valueAsNumber: true })}
                  error={errors.loanTerm?.message}
                />
              </div>
            </div>

            {/* Loan Summary */}
            {watchedValues.loanAmount && watchedValues.interestRate && watchedValues.loanTerm && (
              <div className="bg-blue-50 rounded-lg p-4 mt-6">
                <h4 className="font-medium text-blue-900 mb-2">Loan Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700">Loan Amount:</span>
                    <span className="float-right font-medium">${watchedValues.loanAmount.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">Monthly Payment:</span>
                    <span className="float-right font-medium">
                      ${calculateMonthlyPayment(watchedValues.loanAmount, watchedValues.interestRate, watchedValues.loanTerm).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Renovation Costs Tab */}
        {activeTab === 'renovation' && (
          <div className="space-y-6">
            <p className="text-gray-600">Enter estimated costs for renovations and repairs needed for this property.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="roofCosts" className="block text-sm font-medium text-gray-700">
                  New Roof Costs
                </label>
                <Input
                  id="roofCosts"
                  type="number"
                  placeholder="15000"
                  {...register('roofCosts', { valueAsNumber: true })}
                  error={errors.roofCosts?.message}
                />
              </div>

              <div>
                <label htmlFor="flooringCosts" className="block text-sm font-medium text-gray-700">
                  New Flooring Costs
                </label>
                <Input
                  id="flooringCosts"
                  type="number"
                  placeholder="8000"
                  {...register('flooringCosts', { valueAsNumber: true })}
                  error={errors.flooringCosts?.message}
                />
              </div>

              <div>
                <label htmlFor="paintingCosts" className="block text-sm font-medium text-gray-700">
                  Painting Costs
                </label>
                <Input
                  id="paintingCosts"
                  type="number"
                  placeholder="3000"
                  {...register('paintingCosts', { valueAsNumber: true })}
                  error={errors.paintingCosts?.message}
                />
              </div>

              <div>
                <label htmlFor="kitchenCosts" className="block text-sm font-medium text-gray-700">
                  Kitchen Renovation
                </label>
                <Input
                  id="kitchenCosts"
                  type="number"
                  placeholder="20000"
                  {...register('kitchenCosts', { valueAsNumber: true })}
                  error={errors.kitchenCosts?.message}
                />
              </div>

              <div>
                <label htmlFor="bathroomCosts" className="block text-sm font-medium text-gray-700">
                  Bathroom Renovation
                </label>
                <Input
                  id="bathroomCosts"
                  type="number"
                  placeholder="12000"
                  {...register('bathroomCosts', { valueAsNumber: true })}
                  error={errors.bathroomCosts?.message}
                />
              </div>

              <div>
                <label htmlFor="hvacCosts" className="block text-sm font-medium text-gray-700">
                  HVAC System
                </label>
                <Input
                  id="hvacCosts"
                  type="number"
                  placeholder="6000"
                  {...register('hvacCosts', { valueAsNumber: true })}
                  error={errors.hvacCosts?.message}
                />
              </div>

              <div>
                <label htmlFor="electricalCosts" className="block text-sm font-medium text-gray-700">
                  Electrical Work
                </label>
                <Input
                  id="electricalCosts"
                  type="number"
                  placeholder="4000"
                  {...register('electricalCosts', { valueAsNumber: true })}
                  error={errors.electricalCosts?.message}
                />
              </div>

              <div>
                <label htmlFor="plumbingCosts" className="block text-sm font-medium text-gray-700">
                  Plumbing Work
                </label>
                <Input
                  id="plumbingCosts"
                  type="number"
                  placeholder="5000"
                  {...register('plumbingCosts', { valueAsNumber: true })}
                  error={errors.plumbingCosts?.message}
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="generalFixUpCosts" className="block text-sm font-medium text-gray-700">
                  General Fix-Up Costs
                </label>
                <Input
                  id="generalFixUpCosts"
                  type="number"
                  placeholder="5000"
                  {...register('generalFixUpCosts', { valueAsNumber: true })}
                  error={errors.generalFixUpCosts?.message}
                />
              </div>
            </div>

            {/* Renovation Summary */}
            {totalRenovationCosts > 0 && (
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-medium text-green-900 mb-2">Renovation Summary</h4>
                <div className="text-lg font-semibold text-green-800">
                  Total Renovation Costs: ${totalRenovationCosts.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Analysis Tab */}
        {activeTab === 'analysis' && (
          <div className="space-y-6">
            <div>
              <label htmlFor="analysisType" className="block text-sm font-medium text-gray-700 mb-3">
                Investment Analysis Type
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { value: 'buy-hold', label: 'Buy & Hold', desc: 'Rental income analysis' },
                  { value: 'flip', label: 'Fix & Flip', desc: 'Profit from renovation & sale' },
                  { value: 'brrr', label: 'BRRR', desc: 'Buy-Rehab-Rent-Refinance' },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                      analysisType === option.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      value={option.value}
                      {...register('analysisType')}
                      className="sr-only"
                    />
                    <div className="flex flex-1 flex-col">
                      <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                      <span className="mt-1 text-sm text-gray-500">{option.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.analysisType && (
                <p className="mt-2 text-sm text-red-600">{errors.analysisType.message}</p>
              )}
            </div>

            {/* Analysis-specific fields */}
            {analysisType === 'buy-hold' && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Buy & Hold Parameters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="expectedRentPerMonth" className="block text-sm font-medium text-gray-700">
                      Expected Monthly Rent
                    </label>
                    <Input
                      id="expectedRentPerMonth"
                      type="number"
                      placeholder="2500"
                      {...register('expectedRentPerMonth', { valueAsNumber: true })}
                      error={errors.expectedRentPerMonth?.message}
                    />
                  </div>
                  <div>
                    <label htmlFor="vacancyRate" className="block text-sm font-medium text-gray-700">
                      Vacancy Rate (%)
                    </label>
                    <Input
                      id="vacancyRate"
                      type="number"
                      step="0.1"
                      placeholder="5"
                      {...register('vacancyRate', { valueAsNumber: true })}
                      error={errors.vacancyRate?.message}
                    />
                  </div>
                  <div>
                    <label htmlFor="maintenanceRate" className="block text-sm font-medium text-gray-700">
                      Maintenance Rate (%)
                    </label>
                    <Input
                      id="maintenanceRate"
                      type="number"
                      step="0.1"
                      placeholder="10"
                      {...register('maintenanceRate', { valueAsNumber: true })}
                      error={errors.maintenanceRate?.message}
                    />
                  </div>
                  <div>
                    <label htmlFor="managementRate" className="block text-sm font-medium text-gray-700">
                      Management Rate (%)
                    </label>
                    <Input
                      id="managementRate"
                      type="number"
                      step="0.1"
                      placeholder="8"
                      {...register('managementRate', { valueAsNumber: true })}
                      error={errors.managementRate?.message}
                    />
                  </div>
                </div>
              </div>
            )}

            {analysisType === 'flip' && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Fix & Flip Parameters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="afterRepairValue" className="block text-sm font-medium text-gray-700">
                      After Repair Value (ARV)
                    </label>
                    <Input
                      id="afterRepairValue"
                      type="number"
                      placeholder="650000"
                      {...register('afterRepairValue', { valueAsNumber: true })}
                      error={errors.afterRepairValue?.message}
                    />
                  </div>
                  <div>
                    <label htmlFor="holdingPeriodMonths" className="block text-sm font-medium text-gray-700">
                      Holding Period (Months)
                    </label>
                    <Input
                      id="holdingPeriodMonths"
                      type="number"
                      placeholder="6"
                      {...register('holdingPeriodMonths', { valueAsNumber: true })}
                      error={errors.holdingPeriodMonths?.message}
                    />
                  </div>
                  <div>
                    <label htmlFor="sellingCosts" className="block text-sm font-medium text-gray-700">
                      Selling Costs (Realtor, closing, etc.)
                    </label>
                    <Input
                      id="sellingCosts"
                      type="number"
                      placeholder="40000"
                      {...register('sellingCosts', { valueAsNumber: true })}
                      error={errors.sellingCosts?.message}
                    />
                  </div>
                </div>
              </div>
            )}

            {analysisType === 'brrr' && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">BRRR Parameters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="afterRepairValue" className="block text-sm font-medium text-gray-700">
                      After Repair Value (ARV)
                    </label>
                    <Input
                      id="afterRepairValue"
                      type="number"
                      placeholder="650000"
                      {...register('afterRepairValue', { valueAsNumber: true })}
                      error={errors.afterRepairValue?.message}
                    />
                  </div>
                  <div>
                    <label htmlFor="expectedRentPerMonth" className="block text-sm font-medium text-gray-700">
                      Expected Monthly Rent
                    </label>
                    <Input
                      id="expectedRentPerMonth"
                      type="number"
                      placeholder="2500"
                      {...register('expectedRentPerMonth', { valueAsNumber: true })}
                      error={errors.expectedRentPerMonth?.message}
                    />
                  </div>
                  <div>
                    <label htmlFor="vacancyRate" className="block text-sm font-medium text-gray-700">
                      Vacancy Rate (%)
                    </label>
                    <Input
                      id="vacancyRate"
                      type="number"
                      step="0.1"
                      placeholder="5"
                      {...register('vacancyRate', { valueAsNumber: true })}
                      error={errors.vacancyRate?.message}
                    />
                  </div>
                  <div>
                    <label htmlFor="maintenanceRate" className="block text-sm font-medium text-gray-700">
                      Maintenance Rate (%)
                    </label>
                    <Input
                      id="maintenanceRate"
                      type="number"
                      step="0.1"
                      placeholder="10"
                      {...register('maintenanceRate', { valueAsNumber: true })}
                      error={errors.maintenanceRate?.message}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200">
          <div className="flex space-x-3">
            {activeTab !== 'property' && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  const tabs = ['property', 'loan', 'renovation', 'analysis'];
                  const currentIndex = tabs.indexOf(activeTab);
                  setActiveTab(tabs[currentIndex - 1] as any);
                }}
              >
                Previous
              </Button>
            )}
          </div>

          <div className="flex space-x-3">
            {activeTab !== 'analysis' ? (
              <Button
                type="button"
                onClick={() => {
                  const tabs = ['property', 'loan', 'renovation', 'analysis'];
                  const currentIndex = tabs.indexOf(activeTab);
                  setActiveTab(tabs[currentIndex + 1] as any);
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={!isValid || isLoading}
                className="min-w-[120px]"
              >
                {isLoading ? <LoadingSpinner size="sm" /> : 'Analyze Property'}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

// Helper function to calculate monthly mortgage payment
function calculateMonthlyPayment(loanAmount: number, annualInterestRate: number, loanTermYears: number): number {
  const monthlyInterestRate = annualInterestRate / 100 / 12;
  const numberOfPayments = loanTermYears * 12;
  
  if (monthlyInterestRate === 0) {
    return loanAmount / numberOfPayments;
  }
  
  const monthlyPayment = loanAmount * 
    (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) / 
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
  
  return Math.round(monthlyPayment);
}